import { useState, useCallback, useEffect, useRef } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "info";
}

export function useConfirmDialog() {
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({
    title: "",
    message: "",
  });
  const resolver = useRef<((value: boolean) => void) | null>(null);
  useEffect(() => () => { resolver.current?.(false); resolver.current = null; }, []);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    resolver.current?.(false);
    setOptions(opts);
    setOpened(true);
    setLoading(false);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setOpened(false);
    resolver.current?.(true);
    resolver.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setOpened(false);
    resolver.current?.(false);
    resolver.current = null;
  }, []);

  const ConfirmDialogNode = (
    <ConfirmDialog
      opened={opened}
      title={options.title}
      message={options.message}
      confirmLabel={options.confirmLabel}
      cancelLabel={options.cancelLabel}
      variant={options.variant}
      loading={loading}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, setLoading, ConfirmDialogNode };
}
